import React from "react";
import type { ApolloClient } from "@apollo/client";
import { get, map, size } from "lodash";
import { Card, Col, Form, Row } from "react-bootstrap";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { userService } from "services/userService";
import { toast } from "react-toastify";
import { push } from "store/actions/router";
import { Formik } from "formik";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { hasFullyOnboarded } from "store/selectors/employer";
import { employerConstants } from "actions/types";
import {
  getPaginatedInvites,
  resendInvitation,
  uploadRoster,
} from "actions/employerActions";
import {
  IndividualAddress1Validator,
  individualAddress2Validator,
  NAME_LENGTH_ERROR,
  NAME_MESSAGE,
  noWhitespaceRegex,
  PRINTABLE_ASCII_MESSAGE,
  testFirstLastName,
  testName,
  testPhoneNumber,
  testPrintableAsciiCharacters,
  testSsn,
  INVALID_SSN_ERROR,
  validateUsersAddress,
  whiteSpaceError,
  INVALID_PHONE_NUMBER,
  DOB_FORMAT_ERROR,
  emailValidator,
  testDobFormat,
  parseDob,
  stateValidator,
} from "utils/fieldValidators";

import moment from "moment";
import MaskedInput from "components/MaskedInput";
import StateSelect from "components/StateSelect";
import Button from "components/Button";
import Alert from "components/Alert";
import AddressValidator from "components/AddressValidator";
import UploadProcessing from "pages/dashboards/employerDashboard/userManagement/UploadProcessing";
import { ScrollToFieldError } from "utils/form";
import type { RootState } from "types/state";

import * as yup from "yup";

/** Form values come from initialValues (employeeInfo + groupId) — all strings,
 * matching initialValues rather than the yup schema's coercion. */
interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  dob: string;
  ssn: string;
  confirmSsn: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  phoneNumber: string;
  location: string;
  department: string;
  groupId: string;
  confirmDob: string;
}

interface UploadSingleState {
  fetchingAddress: boolean;
  showAddressModal: boolean;
  validatedAddress: Record<string, unknown>;
  isInviting: boolean;
  groupId: string;
  jobError: string;
  employeeInfo: {
    dob: string;
    email: string;
    ssn: string;
    firstName: string;
    lastName: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    postalCode: string;
    phoneNumber: string;
    location: string;
    department: string;
  };
}

const schema = yup.object({
  firstName: yup
    .string()
    .label("First")
    .matches(noWhitespaceRegex, whiteSpaceError)
    .required()
    .test("is-valid-name", NAME_MESSAGE, testName)
    .min(1, "Must be at least one character.")
    .max(20)
    .test("first-last-name-length", NAME_LENGTH_ERROR, testFirstLastName),
  lastName: yup
    .string()
    .label("Last")
    .matches(noWhitespaceRegex, whiteSpaceError)
    .required()
    .test("is-valid-name", NAME_MESSAGE, testName)
    .min(1, "Must be at least one character.")
    .max(20, "Must be less than 20 characters.")
    .test("first-last-name-length", NAME_LENGTH_ERROR, testFirstLastName),
  email: emailValidator().required(),
  dob: yup
    .string()
    .label("Date Of Birth")
    .required()
    .test("dob-format", DOB_FORMAT_ERROR, testDobFormat)
    .test(
      "dob",
      "Employee must be at least 18 to create an account.",
      (value) => {
        return (
          parseDob(value).isValid() &&
          moment().diff(parseDob(value), "years") >= 18
        );
      }
    )
    .test(
      "dob",
      "Employee must be under 100 years old to create an account.",
      (value) => {
        return (
          parseDob(value).isValid() &&
          moment().diff(parseDob(value), "years") < 100
        );
      }
    ),
  confirmDob: yup
    .string()
    .required()
    .label("Confirm Date of Birth")
    .test("dob-match", "Date of births must match", function (value) {
      return this.parent.dob === value;
    }),
  ssn: yup
    .string()
    .trim()
    .required()
    .label("Social Security Number")
    // @ts-expect-error -- testSsn is passed directly so yup's TestContext lands
    // in its 2nd `allowNoDash` arg (a pre-existing footgun pinned by a
    // characterization test); schema kept byte-identical, fix needs product sign-off
    .test("ssn", INVALID_SSN_ERROR, testSsn),
  confirmSsn: yup
    .string()
    .trim()
    .required()
    .label("Confirm Social Security Number")
    .test("ssn-match", "Social Security Numbers must match", function (value) {
      return this.parent.ssn === value;
    }),
  address1: IndividualAddress1Validator,
  address2: individualAddress2Validator,
  city: yup
    .string()
    .label("City")
    .required()
    .test("city", PRINTABLE_ASCII_MESSAGE, testPrintableAsciiCharacters)
    .max(50)
    .matches(noWhitespaceRegex, whiteSpaceError),
  state: stateValidator,
  postalCode: yup
    .string()
    .label("Zip")
    .required()
    .test("postal-code", PRINTABLE_ASCII_MESSAGE, testPrintableAsciiCharacters)
    .test(
      "postal-code-length",
      "Postal Code must be five digits",
      (val) => size(val) === 5
    )
    .matches(noWhitespaceRegex, whiteSpaceError),
  phoneNumber: yup
    .string()
    .label("Phone")
    .required()
    .test("phone", PRINTABLE_ASCII_MESSAGE, testPrintableAsciiCharacters)
    .test("us-phone", INVALID_PHONE_NUMBER, testPhoneNumber)
    .max(15),
});

const isLoadingSelector = createLoadingSelector(
  employerConstants.UPLOAD_ROSTER
);

const errorSelector = createErrorSelector(employerConstants.UPLOAD_ROSTER);

const mapStateToProps = (state: RootState) => {
  return {
    groups: state.employer.groups,
    error: errorSelector(state),
    isLoading: isLoadingSelector(state),
    hasFullyOnboarded: hasFullyOnboarded(state),
  };
};

const mapDispatchToProps = {
  uploadRoster,
  push,
  getPaginatedInvites,
  resendInvitation,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

class UploadSingle extends React.PureComponent<Props, UploadSingleState> {
  constructor(props: Props) {
    super(props);

    // groups is the loosely-typed redux slice (Record<string, unknown>); the id is
    // realistically a string id — cast at the boundary (matches AddEmployeeForm).
    const defaultGroup = get(props.groups, "0.id") as string;

    this.state = {
      fetchingAddress: false,
      showAddressModal: false,
      validatedAddress: {},
      isInviting: false,
      groupId: defaultGroup,
      jobError: "",
      employeeInfo: {
        dob: "",
        email: "",
        ssn: "",
        firstName: "",
        lastName: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        postalCode: "",
        phoneNumber: "",
        location: "",
        department: "",
      },
    };
  }

  inviteEmployee = async (
    values: FormValues,
    skipAddressValidation = false
  ) => {
    // verify user address, if it matches USPS just continue on
    // if USPS endpoint fails continue on
    // if there is a mismatch then open modal to allow customer to decide which to use
    if (!skipAddressValidation) {
      try {
        this.setState({
          fetchingAddress: true,
        });

        const userAddress = {
          address1: values.address1,
          address2: values.address2,
          state: values.state,
          postalCode: values.postalCode,
          city: values.city,
        };

        const validatedAddress = await userService.getValidatedAddress(
          this.props.client as ApolloClient<object>,
          userAddress
        );
        this.setState({
          fetchingAddress: false,
        });

        const userAddressIsValid = validateUsersAddress(
          userAddress,
          // getValidatedAddress returns nullable string fields; validateUsersAddress
          // narrows internally — cast to its AddressFields param shape at the boundary
          // (no runtime change; value passed verbatim including a possible null)
          validatedAddress as unknown as {
            address1?: string;
            address2?: string;
            city?: string;
            state?: string;
            postalCode?: string;
          }
        );

        if (!userAddressIsValid) {
          this.setState({
            showAddressModal: true,
            validatedAddress: validatedAddress as unknown as Record<
              string,
              unknown
            >,
          });

          return;
        }
      } catch (error) {
        // if the validation endpoint fails we dont want to prevent user from saving so continue on and log error
        console.log(error);
      }
    }

    let employeeInfo = {
      firstName: values.firstName,
      lastName: values.lastName,
      dob: values.dob,
      address1: values.address1,
      address2: values.address2,
      city: values.city,
      state: values.state,
      postalCode: values.postalCode,
      ssn: values.ssn,
      email: values.email,
      phoneNumber: values.phoneNumber,
      location: values.location,
      department: values.department,
    };

    this.setState({
      employeeInfo,
      groupId: values.groupId,
    });

    const result = await this.props.uploadRoster(
      this.props.client as ApolloClient<object>,
      {
        rows: [employeeInfo],
        groupId: values.groupId,
      }
    );

    if (!result.success) return;

    this.setState({ isInviting: true });

    window.analytics.track("Invited single employee to state plan");
  };

  _closeModal = () => {
    this.setState({ showAddressModal: false });
  };

  _renderForm = (): React.ReactNode => {
    const errorMsg = this.props.error || this.state.jobError;
    return (
      <Card>
        <Card.Body>
          <Formik<FormValues>
            validateOnChange={false}
            validationSchema={schema}
            onSubmit={(values) => {
              void this.inviteEmployee(values);
            }}
            enableReinitialize={false}
            initialValues={{
              ...this.state.employeeInfo,
              confirmSsn: "",
              confirmDob: "",
              groupId: this.state.groupId,
            }}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              touched,
              errors,
              setFieldValue,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <ScrollToFieldError />
                {this.state.showAddressModal && (
                  <AddressValidator
                    show={this.state.showAddressModal}
                    // inviteEmployee reads form-shaped values; AddressValidator
                    // resubmits `{...values, ...validatedAddress}` (form shape) at
                    // runtime, so cast its onSubmit/values to the wider modal types
                    onSubmit={
                      this.inviteEmployee as unknown as React.ComponentProps<
                        typeof AddressValidator
                      >["onSubmit"]
                    }
                    setFieldValue={setFieldValue}
                    validatedAddress={this.state.validatedAddress}
                    onClose={this._closeModal}
                    values={
                      values as unknown as React.ComponentProps<
                        typeof AddressValidator
                      >["values"]
                    }
                    allowPoBox={false}
                  />
                )}
                <div id="form-upload-single">
                  <article className="col-form">
                    <Row className="mb-3">
                      <Form.Group as={Col} sm={6} controlId="formBasicGroupId">
                        <Form.Label>Payroll Group</Form.Label>
                        <Form.Select
                          name="groupId"
                          value={values.groupId}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.groupId && !!errors.groupId}
                          isValid={touched.groupId && !errors.groupId}
                        >
                          {map(
                            this.props.groups,
                            (group: { id?: string; name?: string }) => (
                              <option key={group.id} value={group.id}>
                                {group.name}
                              </option>
                            )
                          )}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.groupId}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Row>
                    <Row className="mb-3">
                      <Form.Group as={Col} controlId="formBasicFirstName">
                        <Form.Label>Employee First Name</Form.Label>
                        <Form.Control
                          name="firstName"
                          placeholder="Legal first name"
                          value={values.firstName}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          isInvalid={touched.firstName && !!errors.firstName}
                          isValid={touched.firstName && !errors.firstName}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.firstName}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group as={Col} controlId="formBasicLastName">
                        <Form.Label>Employee Last Name</Form.Label>
                        <Form.Control
                          name="lastName"
                          placeholder="Legal last name"
                          value={values.lastName}
                          onChange={handleChange}
                          isInvalid={touched.lastName && !!errors.lastName}
                          isValid={touched.lastName && !errors.lastName}
                          onBlur={handleBlur}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.lastName}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Row>
                    <Row className="mb-3">
                      {" "}
                      <Form.Group as={Col} controlId="formBasicSsnField">
                        <Form.Label>Employee Social Security Number</Form.Label>
                        <Form.Control
                          as={MaskedInput}
                          mask="000-00-0000"
                          name="ssn"
                          placeholder="000-00-0000"
                          value={values.ssn}
                          onAccept={(val: unknown, mask: unknown) =>
                            handleChange({
                              target: {
                                name: (
                                  mask as { el: { input: HTMLInputElement } }
                                ).el.input.name,
                                value: val,
                              },
                            } as unknown as React.ChangeEvent<HTMLInputElement>)
                          }
                          onBlur={handleBlur}
                          isInvalid={touched.ssn && !!errors.ssn}
                          isValid={touched.ssn && !errors.ssn}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.ssn}
                        </Form.Control.Feedback>
                      </Form.Group>
                      <Form.Group as={Col} controlId="formBasicSsnConfirmField">
                        <Form.Label>
                          Confirm Employee Social Security Number
                        </Form.Label>
                        <Form.Control
                          as={MaskedInput}
                          mask="000-00-0000"
                          name="confirmSsn"
                          placeholder="000-00-0000"
                          value={values.confirmSsn}
                          onAccept={(val: unknown, mask: unknown) =>
                            handleChange({
                              target: {
                                name: (
                                  mask as { el: { input: HTMLInputElement } }
                                ).el.input.name,
                                value: val,
                              },
                            } as unknown as React.ChangeEvent<HTMLInputElement>)
                          }
                          onBlur={handleBlur}
                          isInvalid={touched.confirmSsn && !!errors.confirmSsn}
                          isValid={touched.confirmSsn && !errors.confirmSsn}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.confirmSsn}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Row>

                    <Row className="mb-3">
                      <Form.Group as={Col} controlId="formBasicDateOfBirth">
                        <Form.Label>Date of Birth</Form.Label>
                        <Form.Control
                          as={MaskedInput}
                          mask="00/00/0000"
                          name="dob"
                          placeholder="MM/DD/YYYY"
                          value={values.dob}
                          onAccept={(val: unknown, mask: unknown) =>
                            handleChange({
                              target: {
                                name: (
                                  mask as { el: { input: HTMLInputElement } }
                                ).el.input.name,
                                value: val,
                              },
                            } as unknown as React.ChangeEvent<HTMLInputElement>)
                          }
                          onBlur={handleBlur}
                          isInvalid={touched.dob && !!errors.dob}
                          isValid={touched.dob && !errors.dob}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.dob}
                        </Form.Control.Feedback>
                      </Form.Group>
                      <Form.Group
                        as={Col}
                        controlId="formBasicConfirmDateOfBirth"
                      >
                        <Form.Label>Confirm Date of Birth</Form.Label>
                        <Form.Control
                          as={MaskedInput}
                          mask="00/00/0000"
                          name="confirmDob"
                          placeholder="MM/DD/YYYY"
                          value={values.confirmDob}
                          onAccept={(val: unknown, mask: unknown) =>
                            handleChange({
                              target: {
                                name: (
                                  mask as { el: { input: HTMLInputElement } }
                                ).el.input.name,
                                value: val,
                              },
                            } as unknown as React.ChangeEvent<HTMLInputElement>)
                          }
                          onBlur={handleBlur}
                          isInvalid={touched.confirmDob && !!errors.confirmDob}
                          isValid={touched.confirmDob && !errors.confirmDob}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.confirmDob}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Row>
                    <Row className="mb-3">
                      <Form.Group
                        as={Col}
                        md={4}
                        sm={12}
                        controlId="formBasicEmail"
                      >
                        <Form.Label>Employee Email</Form.Label>
                        <Form.Control
                          name="email"
                          placeholder="user@company.com"
                          value={values.email}
                          onChange={handleChange}
                          isInvalid={touched.email && !!errors.email}
                          isValid={touched.email && !errors.email}
                          onBlur={handleBlur}
                        />
                        <Form.Control.Feedback>
                          {errors.email}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Row>
                    <Row className="mb-3">
                      <Form.Group
                        as={Col}
                        md={4}
                        sm={12}
                        controlId="formBasicLocation"
                      >
                        <Form.Label>Employee Location</Form.Label>
                        <Form.Control
                          name="location"
                          placeholder=""
                          value={values.location}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-3">
                      <Form.Group
                        as={Col}
                        md={4}
                        sm={12}
                        controlId="formBasicDepartment"
                      >
                        <Form.Label>Employee Department</Form.Label>
                        <Form.Control
                          name="department"
                          placeholder=""
                          value={values.department}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Form.Group>
                    </Row>
                    <Form.Label>Employee Address</Form.Label>
                    <Form.Group className="mb-3" controlId="formBasicaddress1">
                      <Form.Control
                        name="address1"
                        placeholder="Address 1"
                        value={values.address1}
                        onChange={handleChange}
                        isInvalid={touched.address1 && !!errors.address1}
                        isValid={touched.address1 && !errors.address1}
                        onBlur={handleBlur}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.address1}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="formBasicaddress2">
                      <Form.Control
                        name="address2"
                        placeholder="Address 2"
                        value={values.address2}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        isInvalid={touched.address2 && !!errors.address2}
                        isValid={touched.address2 && !errors.address2}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.address2}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Row className="mb-3">
                      <Form.Group as={Col} controlId="formBasicCity">
                        <Form.Control
                          name="city"
                          placeholder="City"
                          value={values.city}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.city && !!errors.city}
                          isValid={touched.city && !errors.city}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.city}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group as={Col} sm={4} controlId="formBasicState">
                        <StateSelect
                          value={values.state}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          touched={touched}
                          errors={errors}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.state}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group
                        as={Col}
                        sm={3}
                        controlId="formBasicPostalCode"
                      >
                        <Form.Control
                          name="postalCode"
                          placeholder="Zip"
                          value={values.postalCode}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.postalCode && !!errors.postalCode}
                          isValid={touched.postalCode && !errors.postalCode}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.postalCode}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Row>
                    <Row className="mb-3">
                      <Form.Group as={Col} sm={5} controlId="formBasicPhone">
                        <Form.Control
                          as={MaskedInput}
                          mask="(000) 000-0000"
                          name="phoneNumber"
                          placeholder="Phone"
                          value={values.phoneNumber}
                          onAccept={(val: unknown, mask: unknown) =>
                            handleChange({
                              target: {
                                name: (
                                  mask as { el: { input: HTMLInputElement } }
                                ).el.input.name,
                                value: val,
                              },
                            } as unknown as React.ChangeEvent<HTMLInputElement>)
                          }
                          onBlur={handleBlur}
                          isInvalid={
                            touched.phoneNumber && !!errors.phoneNumber
                          }
                          isValid={touched.phoneNumber && !errors.phoneNumber}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.phoneNumber}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Row>
                    {errorMsg && <Alert type="error" msg={errorMsg} />}
                    <div className="submit-row btn-row">
                      <Button
                        btnLabel={"Go Back"}
                        name="cancel"
                        color={"cancel"}
                        onClick={() =>
                          this.props.push("/dashboard/users/employees")
                        }
                      />
                      <Button
                        withArrow={true}
                        btnLabel={"Invite"}
                        name="submit"
                        loading={
                          this.props.isLoading || this.state.fetchingAddress
                        }
                      />
                    </div>
                  </article>
                </div>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    );
  };

  _onInviteJobCompletion = async (job: unknown, hasErrored: boolean) => {
    //if user is still onboarding and upload did not fail just go to dashboard
    if (!this.props.hasFullyOnboarded && !hasErrored) {
      toast.success("Successfully staged invite.");
      this.props.push("/dashboard");
      return;
    }

    if (!hasErrored) {
      const {
        data: { rows: invites },
      } = (await this.props.getPaginatedInvites(
        this.props.client as ApolloClient<object>,
        {
          limit: 1,
          offset: 0,
          search: this.state.employeeInfo.email,
          groupId: this.state.groupId,
        }
      )) as { data: { rows: unknown[] } };
      const invitation = invites[0];
      // if for some reason invite doesn't exist just go to employees page
      if (!invitation) {
        toast.error(
          "Unable to auto-invite employee, please send invitation manually."
        );
        this.props.push("/dashboard/users/employees");
        return;
      }

      await this.props.resendInvitation(
        this.props.client as ApolloClient<object>,
        // invites comes from the loosely-typed getPaginatedInvites result; the
        // invitation row is the shape resendInvitation expects — cast at boundary
        invitation as Parameters<typeof this.props.resendInvitation>[1]
      );
      toast.success("Successfully invited employee.");
      this.props.push("/dashboard/users/employees");
    } else {
      this.setState({
        isInviting: false,
        jobError: map(
          (job as { errors?: Array<Record<string, unknown>> }).errors,
          "reason"
        ).join(", "),
      });
    }
  };

  render() {
    const content = this.state.isInviting ? (
      <UploadProcessing
        heading={"Processing Invite"}
        subheading={"Give us a moment while we process the invitation."}
        onFinished={this._onInviteJobCompletion}
      />
    ) : (
      this._renderForm()
    );
    return (
      <div className="upload-single">
        <section className="page-title-wrap">
          <article className="text-cell">
            <div className="step-heading">
              <h1 className="page-title">Invite Employee</h1>
              <p className="page-subtext">
                Select a payroll group and add their personal information.
              </p>
            </div>
            <div className="main-content">{content}</div>
          </article>
        </section>
      </div>
    );
  }
}

export default connector(withApollo<Props>(UploadSingle));
